import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TrustedRequest } from '../../models/trusted-request';
import { TrustedRequestService } from '../../services/trusted-request.service';
import { AdminPageHeaderComponent } from '../shared/admin-page-header/admin-page-header.component';

@Component({
  selector: 'app-trusted-requests',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DialogModule,
    TableModule,
    ProgressSpinnerModule,
    AdminPageHeaderComponent,
  ],
  templateUrl: './trusted-requests.component.html',
  styleUrls: ['./trusted-requests.component.scss'],
})
export class TrustedRequestsComponent implements OnInit {
  readonly title = 'Solicitações Trusted';
  readonly subtitle = 'Aprove ou reprove usuários que solicitaram o selo Trusted.';

  requests: TrustedRequest[] = [];
  isLoading = false;
  actionMessage = '';
  actionError = '';
  confirmationVisible = false;
  confirmationAction: 'approve' | 'reject' | null = null;
  selectedRequest: TrustedRequest | null = null;
  trustedRevokedReason = '';
  readonly processingIds = new Set<number>();

  constructor(private trustedRequestService: TrustedRequestService) {}

  ngOnInit(): void {
    this.fetchRequests();
  }

  fetchRequests(): void {
    this.isLoading = true;
    this.actionError = '';
    this.trustedRequestService
      .listRequests()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (requests) => {
          this.requests = requests;
        },
        error: () => {
          this.actionError = 'Não foi possível carregar as solicitações Trusted.';
        },
      });
  }

  approveRequest(request: TrustedRequest): void {
    this.openConfirmation(request, 'approve');
  }

  rejectRequest(request: TrustedRequest): void {
    this.openConfirmation(request, 'reject');
  }

  openConfirmation(request: TrustedRequest, action: 'approve' | 'reject'): void {
    this.selectedRequest = request;
    this.confirmationAction = action;
    this.trustedRevokedReason = '';
    this.confirmationVisible = true;
  }

  closeConfirmation(): void {
    this.confirmationVisible = false;
    this.confirmationAction = null;
    this.selectedRequest = null;
    this.trustedRevokedReason = '';
  }

  confirmSelectedAction(): void {
    const request = this.selectedRequest;
    const action = this.confirmationAction;
    if (!request || !action || this.isProcessing(request.id)) {
      return;
    }

    const trustedRevokedReason = this.trustedRevokedReason.trim();
    if (action === 'reject' && !trustedRevokedReason) {
      this.actionError = 'Informe o motivo da recusa.';
      return;
    }

    this.confirmationVisible = false;
    if (action === 'approve') {
      this.runRequestAction(
        request.id,
        this.trustedRequestService.approveRequest(request.id),
        'Solicitação Trusted aprovada.'
      );
      return;
    }

    this.runRequestAction(
      request.id,
      this.trustedRequestService.rejectRequest(request.id, trustedRevokedReason),
      'Solicitação Trusted reprovada.'
    );
  }

  get confirmationTitle(): string {
    return this.confirmationAction === 'reject'
      ? 'Reprovar solicitação Trusted'
      : 'Aprovar solicitação Trusted';
  }

  get confirmationButtonLabel(): string {
    return this.confirmationAction === 'reject' ? 'Reprovar' : 'Aprovar';
  }

  get confirmationButtonClass(): string {
    return this.confirmationAction === 'reject'
      ? 'admin-btn admin-btn--danger'
      : 'admin-btn admin-btn--success';
  }

  get isConfirmationDisabled(): boolean {
    if (!this.selectedRequest || this.isProcessing(this.selectedRequest.id)) {
      return true;
    }

    return this.confirmationAction === 'reject' && !this.trustedRevokedReason.trim();
  }

  isProcessing(id: number): boolean {
    return this.processingIds.has(id);
  }

  getUserDisplay(request: TrustedRequest): string {
    return request.username || request.email || `Usuário ${request.user_id}`;
  }

  getProgressLabel(current: number, target: number): string {
    return `${current} / ${target}`;
  }

  getCurrencyProgressLabel(current: number | string, target: number | string): string {
    return `${this.formatCurrency(current)} / ${this.formatCurrency(target)}`;
  }

  isCoinPurchaseComplete(request: TrustedRequest): boolean {
    return (
      this.toNumber(request.coin_purchase_value) >=
      this.toNumber(request.coin_purchase_value_target)
    );
  }

  formatCurrency(value: number | string): string {
    const amount = this.toNumber(value);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Number.isFinite(amount) ? amount : 0);
  }

  formatRequestedAt(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private runRequestAction(
    id: number,
    action$: ReturnType<TrustedRequestService['approveRequest']>,
    successMessage: string
  ): void {
    this.processingIds.add(id);
    this.actionMessage = '';
    this.actionError = '';

    action$
      .pipe(finalize(() => this.processingIds.delete(id)))
      .subscribe({
        next: () => {
          this.actionMessage = successMessage;
          this.fetchRequests();
        },
        error: () => {
          this.actionError = 'Não foi possível concluir a ação. Tente novamente.';
        },
      });
  }

  private toNumber(value: number | string): number {
    return typeof value === 'number' ? value : Number(value || 0);
  }
}
